/**
 * ImageLibraryPanel
 *
 * Lets admins generate and browse AI background images via Nano Banana 2
 * (Gemini 3.1 Flash Image). Generated images are stored in Firebase Storage
 * and listed from the Firestore `socialBackgrounds` collection.
 *
 * Props:
 *   onSelectImage(url) — called when user clicks an image to use as canvas background
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Image, Sparkles, Loader2, ChevronDown, ChevronUp, RefreshCw, Wand2, Trash2 } from 'lucide-react';

// Curated prompts mirroring the server-side list — label + description only
// (full prompts live server-side to keep them safe)
const CURATED_PROMPTS = [
  { key: 'professional-workspace',  label: 'Modern Dental Practice Reception',   description: 'Clean desk, blue accents, soft light — great for invoice/admin posts' },
  { key: 'clinical-tools',          label: 'Dental Hygienist Tools Flat Lay',    description: 'Instruments on a tray — clinical, precise feel' },
  { key: 'hygienist-phone',         label: 'Hygienist Checking Phone',           description: 'Confident professional in scrubs — perfect for job/app posts' },
  { key: 'invoice-laptop',          label: 'Invoice on Laptop — Home Setup',     description: 'Self-employed at kitchen table — ideal for invoicing content' },
  { key: 'locum-travel',            label: 'Locum Arriving at Practice',         description: 'On-the-move with suitcase — locum lifestyle content' },
  { key: 'pay-day',                 label: 'Payment Received — Phone Close-Up',  description: 'Bank card + notification screen — pay & invoicing posts' },
  { key: 'wellbeing-break',         label: 'Wellbeing Moment Away from the Chair', description: 'Yoga / tea / natural light — work-life balance content' },
  { key: 'empty-surgery',           label: 'Empty Treatment Room',               description: 'Pristine clinical space — neutral background for any message' },
  { key: 'gdc-registration',        label: 'GDC Proud Moment',                   description: 'Holding registration card — compliance / career milestone posts' },
  { key: 'multi-practice-map',      label: 'Multiple Practices — Map View',      description: 'Planning route on tablet — multi-practice / locum posts' },
];

export default function ImageLibraryPanel({ onSelectImage }) {
  const [library, setLibrary]               = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const [generating, setGenerating]         = useState(false);
  const [pregenRunning, setPregenRunning]   = useState(false);
  const [deletingId, setDeletingId]         = useState(null);
  const [selectedKey, setSelectedKey]       = useState(null);
  const [customPrompt, setCustomPrompt]     = useState('');
  const [useCustom, setUseCustom]           = useState(false);
  const [error, setError]                   = useState(null);
  const [successMsg, setSuccessMsg]         = useState(null);
  const [showPrompts, setShowPrompts]       = useState(true);
  // Lightbox state
  const [previewImage, setPreviewImage]     = useState(null); // { id, url, label }

  const functions  = getFunctions();
  const db         = getFirestore();

  // Live-listen to generated images in Firestore
  useEffect(() => {
    const q = query(
      collection(db, 'socialBackgrounds'),
      orderBy('generatedAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setLibrary(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingLibrary(false);
    }, () => setLoadingLibrary(false));
    return unsub;
  }, [db]);

  const handleGenerate = useCallback(async () => {
    setError(null);
    setSuccessMsg(null);

    if (useCustom) {
      if (!customPrompt.trim()) { setError('Please enter a custom prompt.'); return; }
    } else {
      if (!selectedKey) { setError('Please pick a scene from the list below.'); return; }
    }

    setGenerating(true);
    try {
      const fn = httpsCallable(functions, 'generateBackgroundImage');
      const result = await fn(
        useCustom
          ? { customPrompt: customPrompt.trim() }
          : { promptKey: selectedKey }
      );
      setSuccessMsg(`Generated: "${result.data.label}" — click the image below to use it.`);
    } catch (err) {
      const msg = err?.message || 'Image generation failed.';
      if (msg.includes('not-found') || msg.includes('model')) {
        setError('Model name may need updating (Nano Banana 2 API name). Try again or contact support.');
      } else if (msg.includes('resource-exhausted') || msg.includes('failed-precondition')) {
        setError('Image generation requires a billing-enabled API key (GEMINI_IMAGE_API_KEY). Set this secret in Firebase Secret Manager with your original Google AI Studio key.');
      } else {
        setError(msg);
      }
    } finally {
      setGenerating(false);
    }
  }, [functions, selectedKey, customPrompt, useCustom]);

  const handlePregenerate = useCallback(async () => {
    setError(null);
    setSuccessMsg(null);
    if (!window.confirm('This will generate all 10 starter images using Nano Banana 2 (requires GEMINI_IMAGE_API_KEY). It takes about 2–3 minutes and generates ~10 images. Continue?')) return;

    setPregenRunning(true);
    try {
      const fn = httpsCallable(functions, 'pregenerateBackgrounds');
      const result = await fn({});
      setSuccessMsg(`Pre-generation complete — ${result.data.generated} images created${result.data.errors?.length ? `, ${result.data.errors.length} failed` : ''}.`);
    } catch (err) {
      setError(err?.message || 'Pre-generation failed.');
    } finally {
      setPregenRunning(false);
    }
  }, [functions]);

  const handleDelete = useCallback(async (e, docId) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this background image? This cannot be undone.')) return;
    setDeletingId(docId);
    try {
      const fn = httpsCallable(functions, 'deleteBackgroundImage');
      await fn({ docId });
      // Close lightbox if the deleted image was being previewed
      setPreviewImage(prev => (prev?.id === docId ? null : prev));
    } catch (err) {
      setError(err?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  }, [functions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Generator Card */}
      <Card style={{ background: '#181c24', color: '#fff', border: '1px solid #2d3148' }}>
        <CardHeader style={{ paddingBottom: 8 }}>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
            <Wand2 size={18} style={{ color: '#a78bfa' }} />
            Generate New Background
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>
              Nano Banana 2 · Requires billing key
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Toggle: curated vs custom */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setUseCustom(false)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: !useCustom ? '#7c3aed' : '#23263a', color: !useCustom ? '#fff' : '#a0aec0',
              }}
            >Curated scenes</button>
            <button
              onClick={() => setUseCustom(true)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: useCustom ? '#7c3aed' : '#23263a', color: useCustom ? '#fff' : '#a0aec0',
              }}
            >Custom prompt</button>
          </div>

          {/* Curated scene picker */}
          {!useCustom && (
            <div>
              <button
                onClick={() => setShowPrompts(p => !p)}
                style={{ background: 'none', border: '1px solid #2d3148', borderRadius: 6, color: '#a0aec0', cursor: 'pointer', padding: '6px 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}
              >
                {showPrompts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {selectedKey ? CURATED_PROMPTS.find(p => p.key === selectedKey)?.label : 'Pick a scene…'}
              </button>

              {showPrompts && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                  {CURATED_PROMPTS.map(p => (
                    <button
                      key={p.key}
                      onClick={() => { setSelectedKey(p.key); setShowPrompts(false); }}
                      style={{
                        textAlign: 'left', background: selectedKey === p.key ? '#2d1f6e' : '#23263a',
                        border: selectedKey === p.key ? '1px solid #7c3aed' : '1px solid #2d3148',
                        borderRadius: 8, padding: '10px 14px', cursor: 'pointer', color: '#fff',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{p.label}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Custom prompt */}
          {useCustom && (
            <div>
              <label style={{ fontSize: 12, color: '#9ca3af', display: 'block', marginBottom: 4 }}>
                Describe the scene (max 500 characters)
              </label>
              <Textarea
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value.slice(0, 500))}
                placeholder="e.g. A smiling dental hygienist holding a tablet showing patient records, bright practice background..."
                rows={4}
                style={{ background: '#23263a', color: '#fff', border: '1px solid #2d3148', fontSize: 13 }}
              />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, textAlign: 'right' }}>
                {customPrompt.length}/500
              </div>
            </div>
          )}

          {error   && <div style={{ background: '#3b1515', border: '1px solid #ef4444', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#fca5a5' }}>{error}</div>}
          {successMsg && <div style={{ background: '#14281a', border: '1px solid #22c55e', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#86efac' }}>{successMsg}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={handleGenerate}
              disabled={generating || pregenRunning}
              style={{ background: '#7c3aed', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {generating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Image</>}
            </Button>

            <Button
              onClick={handlePregenerate}
              disabled={generating || pregenRunning || library.length >= 10}
              variant="outline"
              title={library.length >= 10 ? 'Starter images already exist' : 'Generate all 10 starter images at once'}
              style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {pregenRunning ? <><Loader2 size={14} className="animate-spin" /> Running (~2 min)…</> : <><RefreshCw size={14} /> Generate All Starters</>}
            </Button>
          </div>

          <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
            Images are saved to your library permanently and can be reused across sessions.
          </p>
        </CardContent>
      </Card>

      {/* Library grid */}
      <Card style={{ background: '#181c24', color: '#fff', border: '1px solid #2d3148' }}>
        <CardHeader style={{ paddingBottom: 8 }}>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
            <Image size={16} style={{ color: '#60a5fa' }} />
            Image Library
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
              {library.length} image{library.length !== 1 ? 's' : ''}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLibrary ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', padding: '20px 0' }}>
              <Loader2 size={16} className="animate-spin" /> Loading library…
            </div>
          ) : library.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>
              No images yet. Generate your first background above, or click "Generate All Starters" to build the full starter set.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {library.map(img => (
                <div
                  key={img.id}
                  onClick={() => setPreviewImage(img)}
                  title={`${img.label} — click to preview`}
                  style={{
                    cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
                    border: '2px solid transparent', transition: 'border-color 0.15s, transform 0.1s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <img
                    src={img.url}
                    alt={img.label}
                    style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
                    loading="lazy"
                  />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
                    padding: '20px 8px 6px',
                    fontSize: 11, color: '#e5e7eb', lineHeight: 1.3,
                  }}>
                    {img.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lightbox modal */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#181c24', borderRadius: 16, padding: 24,
              border: '1px solid #2d3148', maxWidth: 520, width: '90vw',
              display: 'flex', flexDirection: 'column', gap: 16,
            }}
          >
            {/* Image */}
            <img
              src={previewImage.url}
              alt={previewImage.label}
              style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 10, display: 'block' }}
            />
            {/* Label */}
            <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600, textAlign: 'center' }}>
              {previewImage.label}
            </div>
            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => {
                  onSelectImage && onSelectImage(previewImage.url);
                  setPreviewImage(null);
                }}
                style={{
                  flex: 1, padding: '11px 0', background: '#7c3aed',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                Use as Background
              </button>
              <button
                onClick={() => handleDelete(null, previewImage.id)}
                disabled={deletingId === previewImage.id}
                style={{
                  padding: '11px 18px', background: 'rgba(239,68,68,0.15)',
                  border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8,
                  color: '#fca5a5', fontWeight: 700, fontSize: 14,
                  cursor: deletingId === previewImage.id ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {deletingId === previewImage.id
                  ? <><Loader2 size={14} className="animate-spin" /> Deleting…</>
                  : <><Trash2 size={14} /> Delete</>}
              </button>
              <button
                onClick={() => setPreviewImage(null)}
                style={{
                  padding: '11px 14px', background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#9ca3af', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
